import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Home, Briefcase, FolderOpen, MessageSquare, User, LogOut, LayoutDashboard, BarChart3, Image, Users, Shield, Bell, Edit, Wallet, FileText, Menu, IndianRupee } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import Logo from '../components/Logo';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Navigation = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [walletBalance, setWalletBalance] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [branding, setBranding] = useState({ logo_url: '/taskpiece-logo.png', site_name: 'TaskPiece' });

  const closeMenu = () => setMobileOpen(false);

  useEffect(() => {
    fetchBranding();
    if (user) {
      fetchUnreadCount();
      fetchWalletBalance();
    }
    const interval = user ? setInterval(() => { fetchUnreadCount(); fetchWalletBalance(); }, 30000) : null;
    return () => { if (interval) clearInterval(interval); };
  }, [user]);

  const fetchBranding = async () => {
    try {
      const res = await axios.get(`${API}/settings/branding`);
      setBranding(res.data);
    } catch (e) {}
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(res.data.count || 0);
    } catch (e) {}
  };

  const fetchWalletBalance = async () => {
    try {
      const res = await axios.get(`${API}/wallet`);
      setWalletBalance(res.data.balance ?? 0);
    } catch (e) {}
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    closeMenu();
  };

  return (
    <nav className="border-b bg-white/70 backdrop-blur-xl sticky top-0 z-50" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-4">
        <div className="flex justify-between items-center">
          <div
            className="font-heading text-2xl font-bold text-primary cursor-pointer flex items-center gap-2"
            onClick={() => navigate('/')}
            data-testid="nav-logo"
          >
            <Logo />
          </div>

          <div className="flex items-center gap-6">
            {/* Quick Navigation */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              data-testid="nav-dashboard"
              className="hidden md:flex items-center gap-2"
            >
              <LayoutDashboard className="w-4 h-4" />
              Dashboard
            </Button>

            {user?.role === 'client' || user?.role === 'corporate' ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/gigs')}
                data-testid="nav-gigs"
                className="hidden md:flex items-center gap-2"
              >
                <Briefcase className="w-4 h-4" />
                Find Talent
              </Button>
            ) : null}

            {user?.role === 'freelancer' ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/gigs')}
                  data-testid="nav-gigs"
                  className="hidden md:flex items-center gap-2"
                >
                  <Briefcase className="w-4 h-4" />
                  Browse Gigs
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/projects')}
                  data-testid="nav-projects"
                  className="hidden md:flex items-center gap-2"
                >
                  <FolderOpen className="w-4 h-4" />
                  Find Projects
                </Button>
              </>
            ) : null}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/messages')}
              data-testid="nav-messages"
              className="hidden md:flex items-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              Messages
            </Button>

            {user?.role === 'freelancer' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/portfolio')}
                data-testid="nav-portfolio"
                className="hidden lg:flex items-center gap-2"
              >
                <Image className="w-4 h-4" />
                Portfolio
              </Button>
            )}

            {/* Notification Bell */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/notifications')}
              data-testid="nav-notifications"
              className="relative"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>

            {/* Wallet Balance Widget */}
            {user?.role !== 'admin' && user?.role !== 'super_admin' && walletBalance !== null && (
              <button
                onClick={() => navigate('/wallet')}
                data-testid="nav-wallet-widget"
                className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors cursor-pointer"
              >
                <Wallet className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-700">
                  ₹{walletBalance.toLocaleString('en-IN')}
                </span>
              </button>
            )}

            {/* Desktop User Menu */}
            <div className="hidden md:block">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2"
                >
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.full_name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                      {user?.full_name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <span className="hidden md:inline">{user?.full_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-2">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-secondary capitalize mt-1">{user?.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(`/profile/${user?.id}`)} data-testid="menu-profile">
                  <User className="w-4 h-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile/edit')} data-testid="menu-edit-profile">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/analytics')} data-testid="menu-analytics">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analytics
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/wallet')} data-testid="menu-wallet">
                  <Wallet className="w-4 h-4 mr-2" />
                  Wallet
                </DropdownMenuItem>
                {(user?.role === 'client' || user?.role === 'corporate' || user?.role === 'freelancer') && (
                  <DropdownMenuItem onClick={() => navigate('/invoices')} data-testid="menu-invoices">
                    <FileText className="w-4 h-4 mr-2" />
                    GST Invoices
                  </DropdownMenuItem>
                )}
                {user?.role === 'freelancer' && (
                  <DropdownMenuItem onClick={() => navigate('/portfolio')} data-testid="menu-portfolio">
                    <Image className="w-4 h-4 mr-2" />
                    Portfolio
                  </DropdownMenuItem>
                )}
                {(user?.role === 'client' || user?.role === 'corporate') && (
                  <DropdownMenuItem onClick={() => navigate('/sub-users')} data-testid="menu-sub-users">
                    <Users className="w-4 h-4 mr-2" />
                    Team Members
                  </DropdownMenuItem>
                )}
                {(user?.role === 'admin' || user?.role === 'super_admin') && (
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-admin">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>

            {/* Mobile Hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col h-full">
                  {/* User header */}
                  {user && (
                    <div className="px-5 pt-8 pb-5 border-b bg-slate-50">
                      <div className="flex items-center gap-3">
                        {user.avatar_url ? (
                          <img src={user.avatar_url} alt={user.full_name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                            {user.full_name?.charAt(0) || 'U'}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-sm">{user.full_name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
                        </div>
                      </div>
                      {user.role !== 'admin' && user.role !== 'super_admin' && walletBalance !== null && (
                        <button
                          onClick={() => { navigate('/wallet'); closeMenu(); }}
                          data-testid="mobile-wallet-widget"
                          className="mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                        >
                          <Wallet className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-semibold text-emerald-700">₹{walletBalance.toLocaleString('en-IN')}</span>
                          <span className="text-xs text-emerald-500 ml-auto">Top Up</span>
                        </button>
                      )}
                    </div>
                  )}
                  {/* Nav links */}
                  <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                    {[
                      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', always: true },
                      { icon: Briefcase, label: 'Find Talent / Gigs', path: '/gigs', roles: ['client', 'corporate'] },
                      { icon: Briefcase, label: 'Browse Gigs', path: '/gigs', roles: ['freelancer'] },
                      { icon: FolderOpen, label: 'Projects', path: '/projects', roles: ['freelancer'] },
                      { icon: MessageSquare, label: 'Messages', path: '/messages', always: true },
                      { icon: Bell, label: 'Notifications', path: '/notifications', always: true },
                      { icon: Wallet, label: 'Wallet', path: '/wallet', always: true },
                      { icon: FileText, label: 'GST Invoices', path: '/invoices', roles: ['client', 'corporate', 'freelancer'] },
                      { icon: Image, label: 'Portfolio', path: '/portfolio', roles: ['freelancer'] },
                      { icon: Users, label: 'Team Members', path: '/sub-users', roles: ['client', 'corporate'] },
                      { icon: User, label: 'My Profile', path: `/profile/${user?.id}`, always: true },
                      { icon: Edit, label: 'Edit Profile', path: '/profile/edit', always: true },
                    ]
                      .filter(item => item.always || item.roles?.includes(user?.role))
                      .map(item => (
                        <button
                          key={item.path}
                          onClick={() => { navigate(item.path); closeMenu(); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 text-left text-sm font-medium text-primary transition-colors"
                        >
                          <item.icon className="w-4 h-4 text-muted-foreground" />
                          {item.label}
                        </button>
                      ))
                    }
                  </nav>
                  {/* Logout */}
                  <div className="border-t p-4">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-red-50 text-sm font-medium text-red-600 transition-colors"
                      data-testid="mobile-logout"
                    >
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
